import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { Loader2, MapPin, Moon, Package, ShieldCheck, Sun } from 'lucide-react';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { usePlatform } from '../contexts/PlatformContext';
import { labReport } from '../data/biomarkers';
import { PACK_DAYS, formatAED, pillCountLabel, sachetPillCount } from '../utils/pack';
export function Review() {
  const {
    packPills,
    packPrice,
    placeOrder,
    goal
  } = usePlatform();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  if (!goal) return <Navigate to="/profile" replace data-id="element-436" data-mp="2eeluvox70or8" />;
  if (packPills.length === 0) return <Navigate to="/plan" replace data-id="element-437" data-mp="2ehhtlfgv7hgq" />;
  const morning = packPills.filter(p => p.timing === 'Morning');
  const evening = packPills.filter(p => p.timing === 'Evening');
  const morningCount = sachetPillCount(packPills, 'Morning');
  const eveningCount = sachetPillCount(packPills, 'Evening');
  const arrival = format(addDays(new Date(), 5), 'EEE d MMM');
  const confirm = () => {
    setLoading(true);
    setTimeout(() => {
      placeOrder();
      navigate('/confirmed');
    }, 1000);
  };
  return <div data-id="element-438" data-mp="3armf2lxnjkzq">
      <PageHeader eyebrow="Review" title="Your 1-month pack" description="30 days, pre-portioned into morning and evening sachets." data-id="element-439" data-mp="3arcp2yclxdu1" />

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]" data-id="element-440" data-mp="2e2n6f5t5oh70">
        <div className="space-y-6" data-id="element-441" data-mp="2izx57c2fq57i">
          <section aria-labelledby="calendar-heading" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-442" data-mp="2j186944hwyux">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-443" data-mp="2mzxigtdssacz">
              <h2 id="calendar-heading" className="flex items-center gap-2 font-medium" data-id="element-444" data-mp="2rubgl3jugvlc">
                <Package className="size-4 text-[#9b8cf0]" data-id="element-445" data-mp="1va2up15l8sqf" /> {PACK_DAYS * 2} sachets · {PACK_DAYS} days
              </h2>
              <p className="font-mono text-xs text-muted-foreground" data-id="element-446" data-mp="1zamnjsj4pa6r">
                {morningCount + eveningCount} pills a day · {(morningCount + eveningCount) * PACK_DAYS} in total
              </p>
            </div>
            <dl className="mt-5 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 text-center" data-id="element-447" data-mp="2xmfrl6lkvoet">
              {[{
              label: 'Supplements',
              value: packPills.length
            }, {
              label: 'AM pills / sachet',
              value: morningCount
            }, {
              label: 'PM pills / sachet',
              value: eveningCount
            }].map(s => <div key={s.label} className="bg-[#0f1017] p-4" data-id="element-448" data-mp="2erira5xqyrc7">
                  <dd className="text-2xl font-medium" data-id="element-449" data-mp="2spyvnq8505r6">{s.value}</dd>
                  <dt className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-450" data-mp="2on2k2jh0l9wq">{s.label}</dt>
                </div>)}
            </dl>
          </section>

          <div className="grid gap-6 md:grid-cols-2" data-id="element-451" data-mp="3xmtnplpjn7m">
            {[{
            label: 'Morning sachet',
            icon: Sun,
            items: morning,
            count: morningCount
          }, {
            label: 'Evening sachet',
            icon: Moon,
            items: evening,
            count: eveningCount
          }].map(group => <section key={group.label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-452" data-mp="1ipw2mtjvpky">
                <div className="flex items-center justify-between" data-id="element-453" data-mp="6hj6u7hgd2hf">
                  <h2 className="flex items-center gap-2 font-medium" data-id="element-454" data-mp="6jvpynyy5iw2">
                    <group.icon className="size-4 text-[#9b8cf0]" data-id="element-455" data-mp="u7vpode8kjms" /> {group.label}
                  </h2>
                  <span className="rounded-full border border-white/10 px-2.5 py-0.5 font-mono text-xs" data-id="element-456" data-mp="z5x94zfkqkys">
                    {group.count} {group.count === 1 ? 'pill' : 'pills'}
                  </span>
                </div>
                <ul className="mt-4 divide-y divide-white/5" data-id="element-457" data-mp="peao6bdfp7iu">
                  {group.items.length === 0 && <li className="text-sm text-muted-foreground" data-id="element-458" data-mp="1d44zgoi3uo0z">Nothing in this sachet.</li>}
                  {group.items.map(p => <li key={p.id} className="flex items-center gap-3 py-2.5 text-sm" data-id="element-459" data-mp="3qek1uzqcboqe">
                      <span className="h-5 w-2 shrink-0 rounded-full" style={{
                  backgroundColor: p.color
                }} aria-hidden="true" data-id="element-460" data-mp="3lf7gkjf4appx" />
                      <span className="min-w-0 flex-1" data-id="element-461" data-mp="3ggw5k5d36964">
                        <span className="block" data-id="element-462" data-mp="3ghnzzcuy60ut">{p.name}</span>
                        <span className="font-mono text-[11px] text-muted-foreground" data-id="element-463" data-mp="3bib462m3ljg3">{p.dose}</span>
                      </span>
                      <span className="shrink-0 font-mono text-xs" data-id="element-464" data-mp="d0yuzvb0iz79">{pillCountLabel(p)}</span>
                    </li>)}
                </ul>
              </section>)}
          </div>

          <div className="grid gap-6 md:grid-cols-2" data-id="element-465" data-mp="3ufm1hho662sw">
            <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-466" data-mp="3uf2wf1jmb8wa">
              <h2 className="flex items-center gap-2 font-medium" data-id="element-467" data-mp="vqduhlb1vg9j">
                <MapPin className="size-4 text-[#9b8cf0]" data-id="element-468" data-mp="qydnj8wkbn3g" /> Delivery
              </h2>
              <p className="mt-3 text-sm" data-id="element-469" data-mp="z7jh3trfsny0">
                {goal === 'pregnancy' ? 'Villa 12, Al Barsha 2, Dubai' : 'Apt 1804, Al Reem Island, Abu Dhabi'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground" data-id="element-470" data-mp="13b7n480f7bh5">Monthly · Arrives by {arrival}</p>
            </section>
            <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-471" data-mp="1d7aym4h5wmgr">
              <h2 className="flex items-center gap-2 font-medium" data-id="element-472" data-mp="1d4y8v555x7t5">
                <ShieldCheck className="size-4 text-[#9b8cf0]" data-id="element-473" data-mp="1i5w00785hc6s" /> Doctor verification
              </h2>
              <p className="mt-3 text-sm" data-id="element-474" data-mp="1m6fxxd0jsjtz">{labReport.reviewer}, Family medicine</p>
              <p className="mt-1 text-sm text-muted-foreground" data-id="element-475" data-mp="1r00m6dcc9zfz">Reviews your selection against your results before packing.</p>
            </section>
          </div>
        </div>

        <aside className="h-fit rounded-2xl border border-[#9b8cf0]/40 bg-[#9b8cf0]/[0.05] p-6 lg:sticky lg:top-24" data-id="element-476" data-mp="20xezi42yu842">
          <p className="font-mono text-xs uppercase tracking-widest text-[#c4baff]" data-id="element-477" data-mp="25x1eoj2ctyfi">Order summary</p>
          <dl className="mt-5 space-y-3 text-sm" data-id="element-478" data-mp="1g8vwtewqu5yk">
            <div className="flex justify-between" data-id="element-479" data-mp="1b8ri9ufeblaz"><dt className="text-muted-foreground" data-id="element-480" data-mp="1b9j4wfr0m8p4">Supplements</dt><dd data-id="element-481" data-mp="1b6wu878tvd2o">{packPills.length}</dd></div>
            <div className="flex justify-between" data-id="element-482" data-mp="1pxw4dmpba9od"><dt className="text-muted-foreground" data-id="element-483" data-mp="1pyyrrknfdtzj">Duration</dt><dd data-id="element-484" data-mp="1l6e2mwfgs52c">30 days</dd></div>
            <div className="flex justify-between" data-id="element-485" data-mp="1l5couk72d4gx"><dt className="text-muted-foreground" data-id="element-486" data-mp="1yyc1mefgokks">Delivery</dt><dd data-id="element-487" data-mp="1yx0xuem0ncto">Free</dd></div>
            <div className="flex justify-between border-t border-white/10 pt-3 text-base font-medium" data-id="element-488" data-mp="1z0f0lnyldo1n">
              <dt data-id="element-489" data-mp="1u1bz2dhtlzyf">Total / month</dt><dd data-id="element-490" data-mp="28w8bl2wnx9hb">{formatAED(packPrice)}</dd>
            </div>
          </dl>
          <Button size="lg" disabled={loading} onClick={confirm} className="mt-6 w-full bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" data-id="element-491" data-mp="28ufby7kgvpit">
            {loading ? <Loader2 className="size-4 animate-spin" data-id="element-492" data-mp="ekbplpgzovtn" /> : 'Confirm & send my pack'}
          </Button>
          <Button variant="ghost" className="mt-2 w-full" onClick={() => navigate('/plan')} data-id="element-493" data-mp="9lqmniacry1i">
            Edit pack
          </Button>
          <p className="mt-4 text-center text-xs text-muted-foreground" data-id="element-494" data-mp="2q63vwd1xxaiq">Pause or cancel anytime before the next shipment.</p>
        </aside>
      </div>
    </div>;
}